import { BoondockPoint, ListingPoint, NGRX_BUCKETS } from '@curbnturf/entities';
import { createFeatureSelector, createSelector } from '@ngrx/store';
import { DateTime } from 'luxon';
import { ListingSearchState } from './listing-search.reducer';

// Lookup the 'ListingSearch' feature state managed by NgRx
export const getListingSearchState = createFeatureSelector<ListingSearchState>(NGRX_BUCKETS.listingSearch);

export const getLoaded = createSelector(getListingSearchState, (state: ListingSearchState) =>
  state ? state.loaded : false,
);

export const getError = createSelector(getListingSearchState, (state: ListingSearchState) =>
  state ? state.error : null,
);

export const getAllListingSearchEntities = createSelector(getListingSearchState, (state: ListingSearchState) => {
  if (state && state.searchResults) {
    const instantiatedSearchResults: ListingPoint[] = [];
    for (const key in state.searchResults) {
      if (Object.hasOwnProperty.call(state.searchResults, key)) {
        instantiatedSearchResults[key] = new ListingPoint(state.searchResults[key]);
      }
    }

    return instantiatedSearchResults;
  }

  return [];
});

export const getAllListingSearch = createSelector(
  getListingSearchState,
  getLoaded,
  (state: ListingSearchState, isLoaded): ListingPoint[] => {
    return isLoaded
      ? Object.keys(state.searchResults).map<ListingPoint>(
          (id) => new ListingPoint(state.searchResults[parseInt(id, 10)]),
        )
      : [];
  },
);

export const getAllBoondockSearch = createSelector(
  getListingSearchState,
  getLoaded,
  (state: ListingSearchState, isLoaded): BoondockPoint[] => {
    return isLoaded
      ? Object.keys(state.boondockSearchResults).map<BoondockPoint>(
          (id) => new BoondockPoint(state.boondockSearchResults[parseInt(id, 10)]),
        )
      : [];
  },
);

export const getUserPoints = createSelector(getListingSearchState, (state) => state.userPoints);

export const getAllListingTypeaheadEntities = createSelector(getListingSearchState, (state: ListingSearchState) => {
  if (state && state.typeaheadResults) {
    const instantiatedSearchResults: ListingPoint[] = [];
    for (const key in state.typeaheadResults) {
      if (Object.hasOwnProperty.call(state.typeaheadResults, key)) {
        instantiatedSearchResults[key] = new ListingPoint(state.typeaheadResults[key]);
      }
    }

    return instantiatedSearchResults;
  }

  return {};
});

export const getAllListingTypeahead = createSelector(
  getListingSearchState,
  getLoaded,
  (state: ListingSearchState, isLoaded): ListingPoint[] => {
    return isLoaded
      ? Object.keys(state.typeaheadResults).map<ListingPoint>(
          (id) => new ListingPoint(state.typeaheadResults[parseInt(id, 10)]),
        )
      : [];
  },
);

export const getAllBoondockTypeahead = createSelector(
  getListingSearchState,
  getLoaded,
  (state: ListingSearchState, isLoaded): ListingPoint[] => {
    return isLoaded
      ? Object.keys(state.boondockTypeaheadResults).map<ListingPoint>(
          (id) => new ListingPoint(state.boondockTypeaheadResults[parseInt(id, 10)]),
        )
      : [];
  },
);

export const getSelectedId = createSelector(getListingSearchState, (state: ListingSearchState) => state.selectedId);

export const getSelectedListingSearch = createSelector(
  getAllListingSearchEntities,
  getSelectedId,
  (listingSearch, id) => {
    if (!id) {
      return;
    }

    const result = listingSearch[id];
    return result ? Object.assign({}, result) : undefined;
  },
);

export const getAllPoints = createSelector(getListingSearchState, getLoaded, (state: ListingSearchState, isLoaded) => {
  return isLoaded ? { listings: state.searchResults, boondocks: state.boondockSearchResults } : {listings: [], boondocks: []};
});

export const getListingPoints = createSelector(getListingSearchState, getLoaded, (state: ListingSearchState, isLoaded) => {
  return isLoaded ? state.searchResults : [];
});

export const getBoondockPoints = createSelector(getListingSearchState, getLoaded, (state: ListingSearchState, isLoaded) => {
  return isLoaded ? state.boondockSearchResults : [];
});

export const getSelectedResultId = createSelector(
  getListingSearchState,
  (state: ListingSearchState) => state.selectedResult?.id,
);

export const getSelectedBoondockPoint = createSelector(
  getListingSearchState,
  (state: ListingSearchState) => state.selectedBoondockPoint,
);

export const getSelectedBoondock = createSelector(
  getListingSearchState,
  (state: ListingSearchState) => state.selectedBoondock,
);

export const getSelectedPoint = createSelector(
  getListingSearchState,
  (state: ListingSearchState) => state.selectedResult,
);

export const getSelectedListing = createSelector(getListingSearchState, (state: ListingSearchState) => state.selectedListing);

export const getFilters = createSelector(getListingSearchState, (state: ListingSearchState) => {
  return {
    ...state.filters,
    startDate: state.filters?.startDate ? DateTime.fromISO(state.filters?.startDate) : undefined,
    endDate: state.filters?.endDate ? DateTime.fromISO(state.filters?.endDate) : undefined,
  };
});

export const getSearchMapCenter = createSelector(
  getListingSearchState,
  (state: ListingSearchState) => state.searchMapCenter,
);

export const getSearchMapZoom = createSelector(
  getListingSearchState,
  (state: ListingSearchState) => state.searchMapZoom,
);

export const getSearchMapHeading = createSelector(
  getListingSearchState,
  (state: ListingSearchState) => state.searchMapHeading,
);

export const getSearchListOpen = createSelector(
  getListingSearchState,
  (state: ListingSearchState) => state.searchListOpen,
);

export const getMapType = createSelector(getListingSearchState, (state: ListingSearchState) => state.layerType);
